import { useEffect, useState, useRef } from 'react';
import { useAuthStore } from '@/lib/store';
import { usePathname } from 'next/navigation';

export interface AuthState {
  isAuthenticated: boolean;
  user: any | null;
  isLoading: boolean;
  error: string | null;
}

/**
 * Hook for managing authentication state
 * Uses httpOnly cookies - no token storage in localStorage
 * Authentication state is tracked via API calls and cookie existence
 */
export function useAuth(): AuthState {
  const { user, isAuthenticated } = useAuthStore();
  const pathname = usePathname();
  const initializedRef = useRef(false);

  const [state, setState] = useState<AuthState>({
    isAuthenticated: false,
    user: null,
    isLoading: true,
    error: null
  });

  useEffect(() => {
    if (initializedRef.current && state.isAuthenticated === isAuthenticated && state.isLoading === false) {
      return;
    }
    initializedRef.current = true;

    setState({
      isAuthenticated: Boolean(isAuthenticated && user),
      user: isAuthenticated ? user : null,
      isLoading: false,
      error: null
    });
  }, [isAuthenticated, pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  return state;
}

/**
 * Hook for making authenticated API calls
 * Cookies are automatically included by the browser
 */
export function useAuthenticatedFetch() {
  const authenticatedFetch = async (url: string, options: RequestInit = {}) => {
    // Cookies are automatically included by the browser
    // No need to manually add Authorization header for httpOnly cookies
    // Backend will read tokens from cookies
    return fetch(url, {
      ...options,
      credentials: 'include', // Ensure cookies are sent
    });
  };

  return { authenticatedFetch };
}

